import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { Permission } from '../../models';

@Component({
  selector: 'app-permission-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule
  ],
  template: `
    <h2 mat-dialog-title>{{ data.permission ? 'Editar Permiso' : 'Nuevo Permiso' }}</h2>

    <mat-dialog-content>
      <form [formGroup]="form" class="permission-form">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Código</mat-label>
          <input matInput formControlName="code" placeholder="Ej: reports.export">
          <mat-hint>Formato: módulo.acción (ej: users.read)</mat-hint>
          <mat-error *ngIf="form.get('code')?.hasError('required')">
            El código es requerido
          </mat-error>
          <mat-error *ngIf="form.get('code')?.hasError('pattern')">
            Formato inválido. Use: módulo.acción
          </mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Nombre</mat-label>
          <input matInput formControlName="name" placeholder="Ej: Exportar Reportes">
          <mat-error *ngIf="form.get('name')?.hasError('required')">
            El nombre es requerido
          </mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Módulo</mat-label>
          <mat-select formControlName="module">
            <mat-option *ngFor="let module of data.modules" [value]="module">
              {{ module }}
            </mat-option>
            <mat-option value="Otro">Otro (nuevo módulo)</mat-option>
          </mat-select>
          <mat-error *ngIf="form.get('module')?.hasError('required')">
            El módulo es requerido
          </mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width" *ngIf="form.get('module')?.value === 'Otro'">
          <mat-label>Nombre del nuevo módulo</mat-label>
          <input matInput formControlName="newModule" placeholder="Ej: Integraciones">
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Descripción</mat-label>
          <textarea matInput formControlName="description" rows="3"
                    placeholder="Describe qué permite hacer este permiso"></textarea>
        </mat-form-field>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancelar</button>
      <button mat-raised-button color="primary" (click)="onSave()" [disabled]="form.invalid">
        {{ data.permission ? 'Guardar Cambios' : 'Crear Permiso' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    :host {
      display: block;
    }

    h2[mat-dialog-title] {
      font-family: var(--font-display);
      font-weight: 700;
      letter-spacing: -0.02em;
    }

    .permission-form {
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-width: 400px;
      padding-top: 8px;
    }

    .full-width {
      width: 100%;
    }

    mat-form-field {
      ::ng-deep .mdc-text-field--outlined {
        border-radius: 10px;
      }
    }

    mat-dialog-content {
      padding-top: 0;
    }

    mat-dialog-actions {
      padding: 16px 0 0 0;
    }
  `]
})
export class PermissionDialogComponent {
  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<PermissionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { permission?: Permission; modules: string[] }
  ) {
    const codePattern = /^[a-z]+\.[a-z]+$/;

    this.form = this.fb.group({
      code: [data.permission?.code || '', [Validators.required, Validators.pattern(codePattern)]],
      name: [data.permission?.name || '', Validators.required],
      module: [data.permission?.module || '', Validators.required],
      newModule: [''],
      description: [data.permission?.description || '']
    });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.form.valid) {
      const value = this.form.value;
      const module = value.module === 'Otro' ? value.newModule : value.module;

      this.dialogRef.close({
        code: value.code,
        name: value.name,
        module,
        description: value.description
      });
    }
  }
}
